import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewEncapsulation, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { TranslocoDirective } from '@jsverse/transloco';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { Subject, takeUntil } from 'rxjs';
import { SsPermissionAccessLevel, SsPermissionsClaims } from '../../../../core/auth/auth.types';
import { UserService } from '../../../../core/user/user.service';
import { LicenseService } from '../../../../modules/backoffice/license/license.service';
import { SS_SUPPORTED_MODULES } from '../../../../modules/backoffice/license/modules/modules.constants';
import { AiChatComponent } from '../../../common/ai-chat/ai-chat.component';
import { AI_CONSTANTS } from '../../../common/ai-chat/ai-chat.constants';
import { ThemePickerComponent } from '../../../common/theme-picker/theme-picker.component';
import { WikiComponent } from '../../../common/wiki/wiki.component';

@Component({
    selector: 'classic-layout',
    templateUrl: './classic.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FuseLoadingBarComponent,
        FuseVerticalNavigationComponent,
        MatButtonModule,
        MatIconModule,
        LanguagesComponent,
        FuseFullscreenComponent,
        UserComponent,
        RouterOutlet,
        AiChatComponent,
        ThemePickerComponent,
        WikiComponent,
        MatTooltip,
        TranslocoDirective,
    ],
})
export class ClassicLayoutComponent implements OnInit, OnDestroy {
    readonly aiChat = viewChild<AiChatComponent>('aiChat');
    isScreenSmall: boolean;
    navigation: Navigation;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    protected showAiChat: boolean;

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _userService: UserService,
        private _licenseService: LicenseService
    ) {
        this.checkAiButtonVisibility();

        this._licenseService.activeModules$.pipe(takeUntilDestroyed()).subscribe((_) => {
            this.checkAiButtonVisibility();
        });
    }

    private checkAiButtonVisibility() {
        this.showAiChat =
            this._userService.hasPermission(SsPermissionsClaims.AiServices, SsPermissionAccessLevel.FullAccess) &&
            this._licenseService.isModuleActive(SS_SUPPORTED_MODULES.AI_SERVICES);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    protected readonly AI_CONSTANTS = AI_CONSTANTS;

    toggleAiChat() {
        this.aiChat()?.toggle();
    }
}
